import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import {
  CodeRelevance,
  ComplianceRiskBucket,
  FileComplianceCoverageState,
  FileCompliancePageHit,
  FileComplianceSummary,
  FileComplianceSummaryRow,
  SummaryFreshness,
} from '../../../../generated.commonjs/plan.reviewer_pb';
import { PlanReviewerService } from '../../../shared/plan-reviewer.service';
import {
  CategoricalFilterCriterion,
  ColumnFilterComponent,
  ColumnFilterConfig,
  ColumnFilterCriterion,
  TextFilterCriterion,
} from '../../../shared/table-filter';

type DrilldownFilter = 'high' | 'medium' | 'low' | 'missing' | 'stale';

interface BookGroup {
  bookId: string;
  rows: FileComplianceSummaryRow.AsObject[];
}

interface FileComplianceReturnContext {
  projectId: string;
  fileId: string;
  book: string;
  section: string;
  risk: DrilldownFilter;
  focusId: string;
  tableScrollTop: number;
  windowScrollY: number;
}

/** Aggregated file compliance table with bounded, on-demand page drill-down. */
@Component({
  selector: 'app-file-compliance-summary',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatTooltipModule,
    RouterLink,
    ColumnFilterComponent,
  ],
  templateUrl: './file-compliance-summary.component.html',
  styleUrls: ['./file-compliance-summary.component.scss'],
})
export class FileComplianceSummaryComponent implements OnInit, OnChanges, OnDestroy {
  private planReviewerService = inject(PlanReviewerService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroy$ = new Subject<void>();
  private loadGeneration = 0;
  private requestedQuery: { book: string; section: string; risk: DrilldownFilter } | null = null;
  private focusDrilldownAfterLoad = false;
  private focusOriginId = '';
  private invalidRecoveryPending = false;
  private versionRefreshPending = false;
  private returnContext: FileComplianceReturnContext | null = null;

  @ViewChild('tableShell') private tableShell?: ElementRef<HTMLElement>;

  @Input({ required: true }) projectId = '';
  @Input({ required: true }) fileId = '';

  readonly riskFilters: DrilldownFilter[] = ['high', 'medium', 'low'];
  readonly coverageFilters: DrilldownFilter[] = ['missing', 'stale'];
  readonly sectionFilterConfig: ColumnFilterConfig = {
    columnId: 'file-section',
    columnLabel: 'Section',
    type: 'text',
    placeholder: 'Search sections',
  };
  readonly relevanceFilterConfig: ColumnFilterConfig = {
    columnId: 'file-relevance',
    columnLabel: 'Code relevance',
    type: 'categorical',
    options: ['High', 'Medium', 'Low', 'Not relevant', 'Not evaluated'],
  };
  readonly resultFilterConfig: ColumnFilterConfig = {
    columnId: 'file-compliance',
    columnLabel: 'Compliance',
    type: 'categorical',
    options: ['High Risk', 'Medium Risk', 'Low Risk', 'Missing', 'Stale'],
  };
  summary?: FileComplianceSummary.AsObject;
  loading = true;
  error = '';
  drilldownLoading = false;
  drilldownError = '';
  selectedRow?: FileComplianceSummaryRow.AsObject;
  selectedFilter?: DrilldownFilter;
  pageHits: FileCompliancePageHit.AsObject[] = [];
  totalHitCount = 0;
  nextPageToken = '';
  announcement = '';
  sectionCriterion?: TextFilterCriterion;
  relevanceCriterion?: CategoricalFilterCriterion;
  resultCriterion?: CategoricalFilterCriterion;
  private collapsedRows = new Set<string>();
  private expandedRationales = new Set<string>();

  ngOnInit(): void {
    this.returnContext = this.readReturnContext();
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const risk = params.get('risk');
      const book = params.get('book');
      const section = params.get('section');
      const query = this.isFilter(risk) && book && section ? { book, section, risk } : null;
      this.requestedQuery = query;
      if (!query) {
        this.clearDrilldown(false);
        if ((book || section || risk) && !this.invalidRecoveryPending) {
          this.recoverInvalidQuery();
        }
      } else if (this.summary) {
        void this.restoreQuery(query);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['projectId'] || changes['fileId']) && this.projectId && this.fileId) {
      void this.loadSummary();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get bookGroups(): BookGroup[] {
    const groups = new Map<string, FileComplianceSummaryRow.AsObject[]>();
    for (const row of this.summary?.rowsList ?? []) {
      const rows = groups.get(row.iccBookId) ?? [];
      rows.push(row);
      groups.set(row.iccBookId, rows);
    }
    return Array.from(groups, ([bookId, rows]) => ({ bookId, rows: this.visibleRows(rows) })).filter(
      group => group.rows.length > 0
    );
  }

  get filtersActive(): boolean {
    return (
      Boolean(this.sectionCriterion?.query.trim()) ||
      (this.relevanceCriterion?.values.length ?? 0) > 0 ||
      (this.resultCriterion?.values.length ?? 0) > 0
    );
  }

  get coverageText(): string {
    const coverage = this.summary?.coverage;
    if (!coverage) return 'Coverage is being calculated.';
    return `${coverage.evaluatedPageCount} of ${coverage.ingestedPageCount} ingested pages evaluated; ${coverage.pendingPageCount} pending, ${coverage.failedPageCount} failed, ${coverage.stalePageCount} stale.`;
  }

  get rebuilding(): boolean {
    return this.summary?.freshness === SummaryFreshness.SUMMARY_FRESHNESS_REBUILDING;
  }

  get stale(): boolean {
    return this.summary?.freshness === SummaryFreshness.SUMMARY_FRESHNESS_STALE;
  }

  get noResults(): boolean {
    return !this.loading && !this.rebuilding && (this.summary?.rowsList.length ?? 0) === 0;
  }

  get allEvaluatedLow(): boolean {
    const rows = this.summary?.rowsList ?? [];
    const evaluated = this.summary?.coverage?.evaluatedPageCount ?? 0;
    return (
      evaluated > 0 &&
      rows.every(row => row.highPageCount === 0 && row.mediumPageCount === 0) &&
      rows.some(row => row.lowPageCount > 0)
    );
  }

  relevanceLabel(value: CodeRelevance): string {
    switch (value) {
      case CodeRelevance.CODE_RELEVANCE_HIGH:
        return 'High';
      case CodeRelevance.CODE_RELEVANCE_MEDIUM:
        return 'Medium';
      case CodeRelevance.CODE_RELEVANCE_LOW:
        return 'Low';
      case CodeRelevance.CODE_RELEVANCE_NOT_RELEVANT:
        return 'Not relevant';
      default:
        return 'Not evaluated';
    }
  }

  relevanceClass(value: CodeRelevance): string {
    return `relevance-${this.relevanceLabel(value).toLowerCase().replace(/\s+/g, '-')}`;
  }

  sectionContext(row: FileComplianceSummaryRow.AsObject): string {
    if (row.depth === 0) return this.coverageText;
    const hierarchy = (row.hierarchyList ?? [])
      .map(item => item.title || item.canonicalSectionId)
      .filter(Boolean)
      .join(' > ');
    if (hierarchy) return hierarchy;
    return row.title && row.title !== row.canonicalSectionId ? row.canonicalSectionId : '';
  }

  hasComplianceCounts(row: FileComplianceSummaryRow.AsObject): boolean {
    return [...this.riskFilters, ...this.coverageFilters].some(filter => this.chipCount(row, filter) > 0);
  }

  chipCount(row: FileComplianceSummaryRow.AsObject, filter: DrilldownFilter): number {
    switch (filter) {
      case 'high':
        return row.highPageCount;
      case 'medium':
        return row.mediumPageCount;
      case 'low':
        return row.lowPageCount;
      case 'missing':
        return row.missingPageCount;
      case 'stale':
        return row.stalePageCount;
    }
  }

  chipLabel(filter: DrilldownFilter): string {
    switch (filter) {
      case 'high':
        return 'High';
      case 'medium':
        return 'Medium';
      case 'low':
        return 'Low';
      case 'missing':
        return 'Missing';
      case 'stale':
        return 'Stale';
    }
  }

  complianceContext(row: FileComplianceSummaryRow.AsObject): string {
    const evaluated = row.highPageCount + row.mediumPageCount + row.lowPageCount;
    const coverage = [evaluated + ' evaluated'];
    if (row.missingPageCount > 0) coverage.push(row.missingPageCount + ' missing');
    if (row.stalePageCount > 0) coverage.push(row.stalePageCount + ' stale');
    return coverage.join(' · ');
  }

  chipAriaLabel(row: FileComplianceSummaryRow.AsObject, filter: DrilldownFilter): string {
    const count = this.chipCount(row, filter);
    const pages = `${count} ${count === 1 ? 'page' : 'pages'}`;
    const verb = count === 1 ? 'has' : 'have';
    const section = `Section ${row.canonicalSectionId}: ${row.title}`;
    switch (filter) {
      case 'high':
      case 'medium':
      case 'low':
        return `${pages} ${verb} ${this.chipLabel(filter)} Risk compliance results for ${section}`;
      case 'missing':
        return `${pages} ${verb} no persisted compliance result for ${section}`;
      case 'stale':
        return `${pages} ${verb} compliance results from an older source version for ${section}`;
    }
  }

  chipId(row: FileComplianceSummaryRow.AsObject, filter: DrilldownFilter): string {
    return `file-compliance-chip-${this.domId(row.iccBookId)}-${this.domId(row.canonicalSectionId)}-${filter}`;
  }

  drilldownId(row: FileComplianceSummaryRow.AsObject): string {
    return `file-compliance-results-${this.domId(row.iccBookId)}-${this.domId(row.canonicalSectionId)}`;
  }

  selectedFilterLabel(): string {
    return this.selectedFilter ? this.chipLabel(this.selectedFilter) : 'Matching';
  }

  pageHitIdentity(hit: FileCompliancePageHit.AsObject): string {
    const page = `Page ${hit.pageNumber}`;
    return hit.sheetId ? `${page} · ${hit.sheetId}` : page;
  }

  pageHitMetadata(hit: FileCompliancePageHit.AsObject): string {
    const parts = [this.selectedFilterLabel(), hit.stale ? 'Stale' : 'Current'];
    const seconds = Number(hit.reportCreatedTime?.seconds ?? 0);
    if (seconds > 0) parts.push(new Date(seconds * 1000).toLocaleString());
    return parts.join(' · ');
  }

  isSelected(row: FileComplianceSummaryRow.AsObject, filter: DrilldownFilter): boolean {
    return (
      this.selectedRow?.iccBookId === row.iccBookId &&
      this.selectedRow?.canonicalSectionId === row.canonicalSectionId &&
      this.selectedFilter === filter
    );
  }

  onSectionCriterionChange(criterion?: ColumnFilterCriterion): void {
    this.sectionCriterion = criterion?.type === 'text' ? criterion : undefined;
  }

  onRelevanceCriterionChange(criterion?: ColumnFilterCriterion): void {
    this.relevanceCriterion = criterion?.type === 'categorical' ? criterion : undefined;
  }

  onResultCriterionChange(criterion?: ColumnFilterCriterion): void {
    this.resultCriterion = criterion?.type === 'categorical' ? criterion : undefined;
  }

  isExpanded(row: FileComplianceSummaryRow.AsObject): boolean {
    return this.filtersActive || !this.collapsedRows.has(this.rowKey(row));
  }

  toggleRow(row: FileComplianceSummaryRow.AsObject): void {
    const key = this.rowKey(row);
    if (this.collapsedRows.has(key)) {
      this.collapsedRows.delete(key);
    } else {
      this.collapsedRows.add(key);
    }
  }

  isRationaleExpanded(row: FileComplianceSummaryRow.AsObject): boolean {
    return this.expandedRationales.has(this.rowKey(row));
  }

  toggleRationale(row: FileComplianceSummaryRow.AsObject): void {
    const key = this.rowKey(row);
    if (this.expandedRationales.has(key)) {
      this.expandedRationales.delete(key);
    } else {
      this.expandedRationales.add(key);
    }
  }

  rationaleId(row: FileComplianceSummaryRow.AsObject): string {
    return `file-relevance-${this.domId(row.iccBookId)}-${this.domId(row.canonicalSectionId)}`;
  }

  activateFilter(row: FileComplianceSummaryRow.AsObject, filter: DrilldownFilter): void {
    if (this.chipCount(row, filter) === 0) return;
    this.focusOriginId = this.chipId(row, filter);
    this.focusDrilldownAfterLoad = true;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { book: row.iccBookId, section: row.canonicalSectionId, risk: filter },
    });
  }

  clearDrilldown(updateUrl = true): void {
    const focusOriginId = this.focusOriginId;
    this.selectedRow = undefined;
    this.selectedFilter = undefined;
    this.pageHits = [];
    this.totalHitCount = 0;
    this.nextPageToken = '';
    this.drilldownError = '';
    if (updateUrl) {
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { book: null, section: null, risk: null },
      });
      this.scheduleFocus(focusOriginId);
    }
  }

  loadMore(): void {
    if (this.selectedRow && this.selectedFilter && this.nextPageToken) {
      void this.loadPageHits(this.selectedRow, this.selectedFilter, this.nextPageToken);
    }
  }

  openPage(hit: FileCompliancePageHit.AsObject): void {
    if (!this.selectedRow || !this.selectedFilter) return;
    const context: FileComplianceReturnContext = {
      projectId: this.projectId,
      fileId: this.fileId,
      book: this.selectedRow.iccBookId,
      section: this.selectedRow.canonicalSectionId,
      risk: this.selectedFilter,
      focusId: this.chipId(this.selectedRow, this.selectedFilter),
      tableScrollTop: this.tableShell?.nativeElement.scrollTop ?? 0,
      windowScrollY: globalThis.scrollY ?? 0,
    };
    if (globalThis.history) {
      globalThis.history.replaceState({ ...globalThis.history.state, fileComplianceReturn: context }, '');
    }
    const matchedSectionId = hit.matchedCanonicalSectionId || this.selectedRow.canonicalSectionId;
    void this.router.navigate(
      ['/projects', this.projectId, 'files', this.fileId, 'pages', hit.pageNumber, matchedSectionId],
      {
        queryParams: {
          from: 'file-compliance',
          book: this.selectedRow.iccBookId,
          risk: this.selectedFilter,
        },
        state: { fileComplianceReturn: context },
      }
    );
  }

  retry(): void {
    void this.loadSummary();
  }

  private async loadSummary(): Promise<void> {
    const generation = ++this.loadGeneration;
    this.loading = true;
    this.error = '';
    this.summary = undefined;
    try {
      const response = await this.planReviewerService.getFileComplianceSummary(this.projectId, this.fileId);
      if (generation !== this.loadGeneration) return;
      this.summary = response.toObject();
      if (this.requestedQuery) {
        await this.restoreQuery(this.requestedQuery);
      }
    } catch (error) {
      if (generation === this.loadGeneration) {
        this.error = this.summaryErrorMessage(error);
      }
    } finally {
      if (generation === this.loadGeneration) {
        this.loading = false;
      }
    }
  }

  private async restoreQuery(query: { book: string; section: string; risk: DrilldownFilter }): Promise<void> {
    const row = this.summary?.rowsList.find(
      candidate => candidate.iccBookId === query.book && candidate.canonicalSectionId === query.section
    );
    if (!row || this.chipCount(row, query.risk) === 0) {
      this.recoverInvalidQuery();
      return;
    }
    if (this.isSelected(row, query.risk) && this.pageHits.length > 0) return;
    this.selectedRow = row;
    this.selectedFilter = query.risk;
    this.pageHits = [];
    this.nextPageToken = '';
    await this.loadPageHits(row, query.risk);
  }

  private async loadPageHits(
    row: FileComplianceSummaryRow.AsObject,
    filter: DrilldownFilter,
    pageToken = ''
  ): Promise<void> {
    this.drilldownLoading = true;
    this.drilldownError = '';
    try {
      const response = await this.planReviewerService.listFileCompliancePages({
        projectId: this.projectId,
        fileId: this.fileId,
        bookId: row.iccBookId,
        sectionId: row.canonicalSectionId,
        sourceVersion: this.summary?.sourceVersion ?? '',
        pageSize: 50,
        pageToken,
        ...this.rpcFilter(filter),
      });
      const object = response.toObject();
      if (object.sourceVersion && object.sourceVersion !== this.summary?.sourceVersion) {
        await this.refreshAfterVersionChange();
        return;
      }
      this.pageHits = pageToken ? [...this.pageHits, ...object.pagesList] : object.pagesList;
      this.totalHitCount = object.totalCount;
      this.nextPageToken = object.nextPageToken;
      if (!this.versionRefreshPending) {
        this.announcement = `${object.totalCount} matching ${object.totalCount === 1 ? 'page' : 'pages'} loaded.`;
      }
      if (!pageToken) {
        this.restoreFocusAndScrollOrOpenPanel(row);
      }
    } catch (error) {
      if (this.isVersionChanged(error)) {
        await this.refreshAfterVersionChange();
      } else {
        this.drilldownError = 'Pages for this result could not be loaded. Refresh the summary and try again.';
      }
    } finally {
      this.drilldownLoading = false;
    }
  }

  private rpcFilter(filter: DrilldownFilter): {
    risk?: ComplianceRiskBucket;
    coverageState?: FileComplianceCoverageState;
  } {
    switch (filter) {
      case 'high':
        return { risk: ComplianceRiskBucket.COMPLIANCE_RISK_BUCKET_HIGH };
      case 'medium':
        return { risk: ComplianceRiskBucket.COMPLIANCE_RISK_BUCKET_MEDIUM };
      case 'low':
        return { risk: ComplianceRiskBucket.COMPLIANCE_RISK_BUCKET_LOW };
      case 'missing':
        return {
          coverageState: FileComplianceCoverageState.FILE_COMPLIANCE_COVERAGE_STATE_MISSING,
        };
      case 'stale':
        return {
          coverageState: FileComplianceCoverageState.FILE_COMPLIANCE_COVERAGE_STATE_STALE,
        };
    }
  }

  private isFilter(value: string | null): value is DrilldownFilter {
    return value === 'high' || value === 'medium' || value === 'low' || value === 'missing' || value === 'stale';
  }

  private visibleRows(rows: FileComplianceSummaryRow.AsObject[]): FileComplianceSummaryRow.AsObject[] {
    if (this.filtersActive) {
      const included = new Set<string>();
      for (const row of rows) {
        if (!this.rowMatchesFilters(row)) continue;
        included.add(row.canonicalSectionId);
        for (const ancestor of row.hierarchyList ?? []) {
          included.add(ancestor.canonicalSectionId);
        }
      }
      return rows.filter(row => included.has(row.canonicalSectionId));
    }

    const visible: FileComplianceSummaryRow.AsObject[] = [];
    let collapsedDepth: number | null = null;
    for (const row of rows) {
      if (collapsedDepth !== null && row.depth > collapsedDepth) continue;
      if (collapsedDepth !== null && row.depth <= collapsedDepth) collapsedDepth = null;
      visible.push(row);
      if (row.hasChildren && !this.isExpanded(row)) collapsedDepth = row.depth;
    }
    return visible;
  }

  private rowMatchesFilters(row: FileComplianceSummaryRow.AsObject): boolean {
    const needle = this.sectionCriterion?.query.trim().toLocaleLowerCase() ?? '';
    if (
      needle &&
      !row.canonicalSectionId.toLocaleLowerCase().includes(needle) &&
      !row.title.toLocaleLowerCase().includes(needle)
    ) {
      return false;
    }
    const relevanceValues = this.relevanceCriterion?.values ?? [];
    if (relevanceValues.length > 0 && !relevanceValues.includes(this.relevanceLabel(row.fileRelevance))) {
      return false;
    }
    const resultValues = this.resultCriterion?.values ?? [];
    return (
      resultValues.length === 0 ||
      resultValues.some(value => {
        switch (value) {
          case 'High Risk':
            return row.highPageCount > 0;
          case 'Medium Risk':
            return row.mediumPageCount > 0;
          case 'Low Risk':
            return row.lowPageCount > 0;
          case 'Missing':
            return row.missingPageCount > 0;
          case 'Stale':
            return row.stalePageCount > 0;
          default:
            return false;
        }
      })
    );
  }

  private rowKey(row: FileComplianceSummaryRow.AsObject): string {
    return `${row.iccBookId}\u0000${row.canonicalSectionId}`;
  }

  private recoverInvalidQuery(): void {
    this.clearDrilldown(false);
    this.announcement = 'The requested file compliance filter is unavailable and has been removed.';
    if (this.invalidRecoveryPending) return;
    this.invalidRecoveryPending = true;
    void this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: { book: null, section: null, risk: null },
        replaceUrl: true,
      })
      .finally(() => (this.invalidRecoveryPending = false));
  }

  private async refreshAfterVersionChange(): Promise<void> {
    if (this.versionRefreshPending) return;
    this.versionRefreshPending = true;
    this.announcement = 'File compliance results changed and were refreshed.';
    this.pageHits = [];
    this.nextPageToken = '';
    try {
      await this.loadSummary();
    } finally {
      this.versionRefreshPending = false;
    }
  }

  private isVersionChanged(error: unknown): boolean {
    const candidate = error as { code?: number; message?: string };
    return (
      candidate?.code === 9 &&
      /source version|version changed|page token/i.test(candidate.message ?? String(error ?? ''))
    );
  }

  private summaryErrorMessage(error: unknown): string {
    const code = (error as { code?: number })?.code;
    if (code === 7) return 'You do not have access to this file.';
    if (code === 5) return 'The requested file was not found.';
    return 'The file compliance summary could not be loaded.';
  }

  private restoreFocusAndScrollOrOpenPanel(row: FileComplianceSummaryRow.AsObject): void {
    const context = this.returnContext;
    if (
      context &&
      context.projectId === this.projectId &&
      context.fileId === this.fileId &&
      context.book === row.iccBookId &&
      context.section === row.canonicalSectionId &&
      context.risk === this.selectedFilter
    ) {
      this.returnContext = null;
      this.scheduleAfterRender(() => {
        if (this.tableShell) this.tableShell.nativeElement.scrollTop = context.tableScrollTop;
        globalThis.scrollTo?.({ top: context.windowScrollY, behavior: 'auto' });
        globalThis.document?.getElementById(context.focusId)?.focus();
      });
      return;
    }
    if (this.focusDrilldownAfterLoad) {
      this.focusDrilldownAfterLoad = false;
      this.scheduleFocus(this.drilldownId(row));
    }
  }

  private readReturnContext(): FileComplianceReturnContext | null {
    const value = (globalThis.history?.state as { fileComplianceReturn?: unknown } | undefined)?.fileComplianceReturn;
    if (!value || typeof value !== 'object') return null;
    const candidate = value as Partial<FileComplianceReturnContext>;
    const risk = candidate.risk;
    if (
      typeof candidate.projectId !== 'string' ||
      typeof candidate.fileId !== 'string' ||
      typeof candidate.book !== 'string' ||
      typeof candidate.section !== 'string' ||
      typeof candidate.focusId !== 'string'
    ) {
      return null;
    }
    if (!risk || !this.isFilter(risk)) return null;
    return {
      projectId: candidate.projectId,
      fileId: candidate.fileId,
      book: candidate.book,
      section: candidate.section,
      risk,
      focusId: candidate.focusId,
      tableScrollTop: Number(candidate.tableScrollTop) || 0,
      windowScrollY: Number(candidate.windowScrollY) || 0,
    };
  }

  private scheduleFocus(id: string): void {
    if (!id) return;
    this.scheduleAfterRender(() => globalThis.document?.getElementById(id)?.focus());
  }

  private scheduleAfterRender(action: () => void): void {
    if (globalThis.requestAnimationFrame) {
      globalThis.requestAnimationFrame(() => globalThis.requestAnimationFrame(action));
    } else {
      globalThis.queueMicrotask(action);
    }
  }

  private domId(value: string): string {
    return value.replace(/[^a-zA-Z0-9_-]/g, '-');
  }
}
